// Parsing pasted or uploaded spreadsheet data.
//
// Pure: this decides what a teacher's numbers mean, testable without a
// browser, a file input or Firestore.
//
// Not a regex split. A CSV field may contain the delimiter, a newline, or a
// quote — `"Smith, J.",12.00` is two fields, not three — and a naive split
// shifts every column after the comma. The core scans characters.
//
// **Adapters.** The scanner, the matching, the readings of an amount and a
// date, the category resolution (#110's key, `categoryKey`'s), the row
// judgements and the wizard's sentences are `core/src/expense_import.rs`, so a
// phone importing the same file lands the same rows and refuses the same ones.
// What stays here is the coercion, and the constants a module reads at load,
// which `test/core/expenses.wasm.test.js` holds to the core's.

import { coreSync } from './core.js';

const text = (v) => (v == null ? null : String(v));

// The file the wizard's "Download a sample" button hands out.
//
// Here rather than in the page because it is the worked example of the format
// this module parses, and a test asserts that it imports cleanly against the
// stock categories.
export const SAMPLE_CSV = [
  'Date,Payee,Expense,Revenue,Category,Description',
  '2026-03-04,Sheet Music Plus,62.40,,Teaching Supplies,Method books',
  '2026-03-11,Riverside Arts Council,,250.00,Gig,Spring recital',
  '2026-04-01,Northside Studio,400.00,,Rent,April studio rent',
].join('\n');

// Fields a column can be imported into. `skip` is a real choice, not the absence
// of one: spreadsheets carry running balances and reference numbers that must be
// deliberately dropped rather than guessed at.
export const IMPORT_FIELDS = [
  { key: 'occurredOn',  label: 'Transaction Date' },
  { key: 'amount',      label: 'Expense & Revenue' },
  { key: 'expense',     label: 'Expense' },
  { key: 'revenue',     label: 'Revenue' },
  { key: 'payee',       label: 'Payee' },
  { key: 'category',    label: 'Category' },
  { key: 'description', label: 'Description' },
  { key: 'skip',        label: "Don't import" },
];

// Tab first: pasting from a spreadsheet yields tab-separated text, and a
// description containing a comma would otherwise be read as a column break.
//
// Counted over the first **record** and outside quotes (#146), so these two
// shapes pick the right delimiter for the whole file:
//
//   "Date, Posted";Amount    a semicolon file whose first field is quoted and
//                            holds commas; counting inside quotes would make
//                            it a comma file.
//   "Jan 1⏎Jan 2",Amount     a quoted header cell containing a newline; the
//                            first physical line stops half way through the
//                            record.
//
// The scan walks to the first newline that is not inside a quoted field, the
// same notion of "a row" parseDelimited uses.
export function detectDelimiter(text) {
  return coreSync().expenseDetectDelimiter(String(text || ''));
}

// RFC4180-ish scan. Quotes protect delimiters and newlines; a doubled quote
// inside a quoted field is a literal quote.
//
// ── Each row carries the line it came from (#146) ───────────────────────────
//
// `row.line` is the 1-based physical line the row started on. It is a
// **non-enumerable** property hung on the array rather than a change of shape,
// because every caller indexes these as arrays — `rows[0]` is the header row,
// `r[i]` is a cell — and `.filter` and `.slice` keep the same array objects
// and so keep the property. Non-enumerable so that a row remains exactly its
// cells to anything that spreads, serialises or compares it.
//
// `buildImportRows` reports it so a refused row can be found in the teacher's
// own file. A count derived from the rows' index would be wrong: a blank line
// in the middle of the sheet is dropped (every later row one line early), and
// a quoted field containing a newline spans two physical lines (every later
// row one line late).
//
// ── Where a quote may open a field ──────────────────────────────────────────
//
// At the start of a field, or after nothing but spaces. RFC4180 says the first
// alone, but hand-edited files and some exporters write `a, "b,c"`, which the
// strict reading makes three fields rather than two. A field of
// spaces-then-quote is malformed under the strict reading too, and every
// consumer trims the cell before using it.
//
// Spaces only, and only where the delimiter is not one — a tab-separated file's
// leading tab is a column break, not padding.
export function parseDelimited(text, delimiter = null) {
  const rows = [];
  const d = delimiter == null ? null : String(delimiter);
  for (const cell of coreSync().expenseCsvCells(String(text || ''), d)) {
    if (!rows[cell.row]) {
      rows[cell.row] = [];
      // Non-enumerable so a row still deep-equals a plain array of strings in
      // tests and spreads cleanly into a mapping preview.
      Object.defineProperty(rows[cell.row], 'line', { value: cell.line });
    }
    rows[cell.row][cell.column] = cell.text;
  }
  return rows;
}

// Guess which field each column holds. Exact alias first across every column,
// then a contains-match, so a confident match is never beaten by a loose one in
// an earlier column.
export function autoMatch(headers) {
  return coreSync().expenseAutoMatch(headers.map((h) => String(h || '')));
}

// Money as typed by a human or a bank: "$1,234.56", "(42.50)" for a negative,
// a trailing minus, a stray space, a currency code sitting beside the figure
// ("USD 400.00", "400.00 USD").
//
// The scale comes from the currency, not from a hard-coded hundred: a yen
// studio importing "1200" means twelve hundred yen, not ¥120,000. The same
// table drives fromDecimal(), so a figure typed into the expense form and the
// same figure imported land on the same integer.
//
// ── Noise is named, not excluded (#165) ─────────────────────────────────────
//
// Not `s.replace(/[^0-9.\-]/g, '')`, which cannot tell noise from an absurd
// number: "1e400" would come back as 1400 and "12abc34" as 1234. The noise
// stripped is named — a currency symbol, a three-letter code standing on its
// own beside the figure, and the commas or spaces that group thousands — and
// what is left has to match a plain number or the cell is refused. A
// three-letter code is only stripped when it is its own word (bounded by a
// space, the string's edge, or punctuation): "USD 400.00" loses the "USD",
// "12abc34" keeps its "abc".
//
// "1e400" is refused outright rather than parsed as a float and handed to the
// ceiling in #155, as `entryErrors` in useExpenseEntryForm.js refuses the same
// cell: it is not an amount.
//
// ── Which mark is the decimal point (#532) ──────────────────────────────────
//
// Read as a thousands group, a German bank's `12,50` would import as €1,250.
// `decimalSeparator` is the studio's Region's (`,` in Germany): the figure
// says which mark is the point where it can — both marks, a repeated one, or a
// lone one before anything but three digits — and the Region decides only the
// lone mark before three digits. `parse_amount_minor` in
// core/src/expense_import.rs has the argument.
export function parseAmountMinor(raw, currency = 'USD', decimalSeparator = '.') {
  return coreSync().expenseParseAmountMinor(text(raw), text(currency), text(decimalSeparator)) ?? null;
}

// Dates as exported by anything. ISO is read as itself; otherwise in the
// studio's date order (#532) — `datePattern` is its short-date pattern, so a
// British studio's `03/04/2026` is the third of April and an American one's
// the fourth of March. A first part that cannot be the order's month is read
// the other way round rather than producing a nonsense month.
//
// ── Every reading is checked against a real calendar (#146) ─────────────────
//
// These must not come out of here:
//
//   `2026-13-45`  and `2026-00-00` satisfy the `^[0-9]{4}-[0-9]{2}-[0-9]{2}$`
//                 in firestore.rules, so they would be stored, and
//                 `monthBounds` never produces a range that contains them: the
//                 entry would sum into no month and appear on no screen.
//
//   `04/31/2026`  April 31st sorts past `2026-04-30`, where
//                 `monthBounds('2026-04')` ends, so it lands in no month.
//
//   `3/4/999`     would read `999-03-04`, which fails the rules regex: a
//                 `permission-denied` on a batch of 400 rows naming none of
//                 them.
//
// So the year must be two digits or four, and the assembled y-m-d must be a day
// that exists. A row that cannot be read is reported rather than dropped, and
// the preview names its date cell.
//
// The trailing part of each pattern is unanchored *except* against more
// digits: `2026-03-04T09:00:00Z` is a date with a time after it and is
// read, while `2026-03-041` is not a date at all and is refused rather than
// quietly truncated to the 4th.

export function parseDateISO(raw, datePattern = 'M/d/y') {
  return coreSync().expenseParseDate(text(raw), text(datePattern)) ?? null;
}

// ── The category a row is asking for, and whether the studio has it (#134) ──
//
// Category is required on the add/edit form (#116), as MMS requires it. An
// import that wrote whatever was in the Category column would admit rows the
// form refuses, and categories the studio has never heard of: names that
// appear in no list and that `renameCategory` cannot reach (it matches
// `e.category === from` exactly).
//
// **Such a row is refused** — the row, not the file. The refusal arrives in
// the preview before any write, names the offending cell, and has two remedies
// one step back: the **Default Expense/Revenue Category** selects on step 2
// (one category for every row whose own column is empty), or mapping the
// column to "Don't import". `buildImportRows` reports unreadable rows rather
// than dropping them, so an import never lands 47 of 50 without saying so.
//
// MMS's import is not covered by `research/mms-capture/`; this follows the
// form's rule (#116), not a comparison with MMS's import.
//
// Matching uses **#110's `categoryKey`**: case, surrounding whitespace,
// internal runs of whitespace and Unicode composition fold, and a typo does
// not ("Lessson" is a different word). A spreadsheet saying "teaching
// supplies" lands under the studio's own "Teaching Supplies", spelled the
// studio's way, so the row stays inside the next rename.
//
// The verdicts are #110's vocabulary too:
//
//   `match`      — exactly one of the studio's categories answers to this key.
//   `unset`      — the cell is empty and no default was chosen for this type.
//   `unmatched`  — the studio has no such category. **Nothing is invented.**
//   `ambiguous`  — two of the studio's own categories answer to this key,
//                  differing only by case or spacing. Neither is chosen
//                  (#110). The expense category editor refuses such a
//                  duplicate (`expenseCategoryErrors` in domain/expenses.js),
//                  but two offline devices can each add one without seeing the
//                  other's write.
export function resolveImportCategory(raw, categories = []) {
  const labels = categories.map((c) => String(c ?? ''));
  const r = coreSync().expenseResolveImportCategory(String(raw ?? ''), labels);
  const out = { verdict: r.verdict, category: r.category, asked: r.asked };
  if (r.verdict === 'ambiguous') {
    out.candidates = Array.from(
      coreSync().expenseImportCategoryCandidates(String(raw ?? ''), labels),
      (i) => categories[i],
    );
  }
  return out;
}

// Turn matched columns into entry drafts, flagging what could not be read.
//
// A row with no usable amount or no usable date is reported rather than
// dropped, and so is a row whose category the studio does not have (#134,
// `resolveImportCategory` above).
const PROBLEMS = [
  ['problemDate', 'date'], ['problemAmount', 'amount'], ['problemCategory', 'category'],
  ['problemPayee', 'payee'], ['problemDescription', 'description'],
];

export function buildImportRows({
  rows,
  mapping,
  hasHeader = true,
  defaultExpenseCategory = '',
  defaultRevenueCategory = '',
  expenseCategories = [],
  revenueCategories = [],
  currency = 'USD',
  // The studio's short-date pattern and decimal separator (#532): the order
  // a numeric date is read in, and the mark a lone separator before three
  // digits is read as. The defaults are the American reading.
  datePattern = 'M/d/y',
  decimalSeparator = '.',
}) {
  const cells = rows.flatMap((r, row) => Array.from(r, (cell, column) => ({
    row, column, text: String(cell ?? ''), line: typeof r.line === 'number' ? r.line : 0,
  })));
  return coreSync()
    .expenseImportRows(
      cells, rows.length, mapping.map(String), Boolean(hasHeader),
      String(defaultExpenseCategory ?? ''), String(defaultRevenueCategory ?? ''),
      expenseCategories.map(String), revenueCategories.map(String), text(currency),
      text(datePattern), text(decimalSeparator),
    )
    .map((r) => ({
      line: r.line,
      occurredOn: r.occurredOn ?? null,
      type: r.entryType ?? null,
      amountMinor: r.amountMinor ?? null,
      amountVerdict: r.amountVerdict ?? null,
      category: r.category,
      categoryVerdict: r.categoryVerdict ?? null,
      categoryAsked: r.categoryAsked,
      payee: r.payee,
      description: r.description,
      problems: PROBLEMS.filter(([flag]) => r[flag]).map(([, name]) => name),
    }));
}

const importRowOf = (r) => ({
  line: Number(r.line) || 0,
  occurredOn: r.occurredOn ?? null,
  entryType: r.type ?? null,
  amountMinor: r.amountMinor ?? null,
  amountVerdict: r.amountVerdict ?? null,
  category: String(r.category ?? ''),
  categoryVerdict: r.categoryVerdict ?? null,
  categoryAsked: String(r.categoryAsked ?? ''),
  payee: String(r.payee ?? ''),
  description: String(r.description ?? ''),
  ...Object.fromEntries(PROBLEMS.map(([flag, name]) => [flag, r.problems.includes(name)])),
});

export function importSummary(built) {
  const s = coreSync().expenseImportSummary(built.map(importRowOf));
  return {
    total: s.total,
    importable: s.importable,
    skipped: s.skipped,
    expenseCount: s.expenseCount,
    revenueCount: s.revenueCount,
  };
}

// ── The wizard's own rules ─────────────────────────────────────────────────
//
// Here rather than in `src/pages/ExpenseImportPage.jsx` and the step
// components, so the phones' import says and decides the same things.

// The Match step's column headings: the file's own first row, or `Column N`
// when the first row is data.
export function importHeaders(rows, hasHeader) {
  if (!rows.length) return [];
  return coreSync().expenseImportHeaders(Array.from(rows[0], (h) => String(h ?? '')), Boolean(hasHeader));
}

// Matching column `index` to `field`. A field can only be filled from one
// column; claiming one releases it from whichever column held it. `skip` is
// never claimed.
export function claimImportColumn(mapping, index, field) {
  return coreSync().expenseClaimImportColumn(Array.from(mapping, (v) => String(v ?? '')), index, String(field));
}

// A spreadsheet file the wizard cannot read, refused before it is read.
export function importFileRefusal(name) {
  return coreSync().expenseImportFileRefusal(String(name ?? ''))?.message ?? null;
}

// Next on the Paste step with nothing in the box.
export const NOTHING_TO_IMPORT = 'There is nothing to import yet.';

// Why this row's category was refused, in the cell where the category would
// have been (#134): what the sheet says and that the studio has no such
// category, pointing at the two remedies — set a default on step 2, or map the
// column to "Don't import" on step 3.
export function importCategoryProblemText(row) {
  return coreSync().expenseImportCategoryProblem(text(row.categoryVerdict), String(row.categoryAsked ?? ''));
}

// A cell too long for what firestore.rules will store (#145), named with both
// numbers ("312 characters, limit 199") so the teacher can find the column.
export function importTooLongText(value, limit) {
  return coreSync().expenseImportTooLong(String(value), limit);
}

// An amount past the one-row ceiling, around the ceiling in the studio's money
// (#472).
export function importTooLargeText(ceilingText) {
  return coreSync().expenseImportTooLarge(String(ceilingText));
}

// Above the preview, when some rows will be left out.
const summaryOf = (s) => ({
  total: Number(s.total) || 0,
  importable: Number(s.importable) || 0,
  skipped: Number(s.skipped) || 0,
  expenseCount: Number(s.expenseCount) || 0,
  revenueCount: Number(s.revenueCount) || 0,
});

export function importSkippedNotice(summary) {
  return coreSync().expenseImportSkippedNotice(summaryOf(summary)) ?? null;
}

// The Import button: `Import 12`, or bare `Import` with nothing importable.
export function importButtonLabel(summary) {
  return coreSync().expenseImportButtonLabel(summaryOf(summary));
}

// The card once the import has landed: a heading and what was added.
export function importDoneText(done, summary) {
  const { title, detail } = coreSync().expenseImportDone(Number(done) || 0, summaryOf(summary));
  return { title, detail };
}
